import { InterstitialCard } from '@/api/generated-catch-backend/model';
import { animate, motion, useMotionValue, useTransform } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import { StickyFooterBar } from '../../../sticky-footer-bar/sticky-footer-bar';
import { NextButton } from '../next-button/button';
import { BackgroundAsset, GraphicLottie } from './interstitial-shared';

type InterstitialCardStackProps = {
  title: string;
  cards: InterstitialCard[];
  cardStackTimeSeconds: number;
  backgroundAsset?: string;
  backgroundOpacity?: number;
  onNext?: () => void;
  onBack?: () => void;
  isBackDisabled?: boolean;
};

// Constants for animation and interaction thresholds
const SWIPE_CONFIDENCE_THRESHOLD = 10000;
const DRAG_DETECTION_THRESHOLD = 10;
const SLIDE_DISTANCE = 400;
const DRAG_CONSTRAINT = 50;

export const InterstitialCardStack = ({
  title,
  cards,
  cardStackTimeSeconds,
  backgroundAsset,
  backgroundOpacity,
  onNext,
  onBack,
  isBackDisabled,
}: InterstitialCardStackProps) => {
  const [isAutoRotating, setIsAutoRotating] = useState(true);
  const [cardState, setCardState] = useState<{
    currentCardIndex: number;
    animatingCardIndex: number | null;
    direction: 'forward' | 'backward';
  }>({
    currentCardIndex: 0,
    animatingCardIndex: null,
    direction: 'forward',
  });

  // Track drag direction to determine which adjacent cards to render
  const [dragDirection, setDragDirection] = useState<'forward' | 'backward' | null>(null);

  // Progress for auto-advance animation
  const progress = useMotionValue(0);
  const fillWidth = useTransform(progress, [0, 1], ['0%', '100%']);

  // Auto-rotate cards based on cardStackTimeSeconds
  useEffect(() => {
    if (cards.length <= 1 || !isAutoRotating) {
      // Reset progress when not auto-rotating
      animate(progress, 0, { duration: 0.2 });
      return;
    }

    let animationControls: ReturnType<typeof animate> | null = null;

    const startProgress = () => {
      // Start progress animation
      animationControls = animate(progress, 1, {
        duration: cardStackTimeSeconds,
        ease: 'linear',
      });
    };

    const interval = setInterval(() => {
      setCardState((prev) => {
        const nextIndex = prev.currentCardIndex + 1;

        // If we're at the last card, stop auto-rotation
        if (nextIndex >= cards.length) {
          setIsAutoRotating(false);
          return prev; // Don't change the card state
        }

        return {
          currentCardIndex: nextIndex,
          animatingCardIndex: prev.currentCardIndex,
          direction: 'forward',
        };
      });

      // Reset progress and start again
      progress.set(0);
      startProgress();
    }, cardStackTimeSeconds * 1000);

    // Start initial progress
    startProgress();

    return () => {
      clearInterval(interval);
      if (animationControls) {
        animationControls.stop();
      }
    };
  }, [cards.length, cardStackTimeSeconds, isAutoRotating, progress]);

  const handleNextCard = () => {
    if (cardState.currentCardIndex < cards.length - 1) {
      const nextIndex = cardState.currentCardIndex + 1;
      setCardState((prev) => ({
        currentCardIndex: nextIndex,
        animatingCardIndex: prev.currentCardIndex,
        direction: 'forward',
      }));
      setIsAutoRotating(false);
    } else {
      // If we're at the last card, go to next questionnaire item
      onNext?.();
    }
  };

  const handlePreviousCard = () => {
    if (cardState.currentCardIndex > 0) {
      const prevIndex = cardState.currentCardIndex - 1;
      setCardState((prev) => ({
        currentCardIndex: prevIndex,
        animatingCardIndex: prev.currentCardIndex,
        direction: 'backward',
      }));
      setIsAutoRotating(false);
    }
  };

  const handleDotClick = (index: number) => {
    if (index !== cardState.currentCardIndex) {
      setCardState((prev) => ({
        currentCardIndex: index,
        animatingCardIndex: prev.currentCardIndex,
        direction: index > prev.currentCardIndex ? 'forward' : 'backward',
      }));
      setIsAutoRotating(false);
    }
  };

  const graphic = useMemo(() => {
    const url = cards[cardState.currentCardIndex].image;
    if (!url) return null;

    if (url.includes('.json')) {
      return <GraphicLottie srcUrl={url} className="interstitial-card-stack-graphic" />;
    }
    return <img src={url} alt="Chart illustration" className="interstitial-card-stack-card-image" draggable={false} />;
  }, [cardState.currentCardIndex, cards]);

  // Reset animating card after animation completes
  const handleAnimationComplete = () => {
    setCardState((prev) => ({ ...prev, animatingCardIndex: null }));
  };

  const swipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
  };

  if (cards.length === 0) {
    return null;
  }

  return (
    <div className="interstitial-card-stack-container">
      <div className="interstitial-card-stack-background" />

      {/* Background asset (video or image) */}
      <BackgroundAsset backgroundAsset={backgroundAsset} classNamePrefix="interstitial-card-stack" />

      {/* Background overlay for opacity control */}
      <div
        className="interstitial-card-stack-overlay"
        style={{
          opacity: backgroundAsset ? (backgroundOpacity ?? 0.6) : undefined,
          backgroundColor: backgroundAsset ? 'rgba(0, 0, 0, 0.4)' : undefined,
          backdropFilter: backgroundAsset ? 'blur(12px)' : undefined,
          WebkitBackdropFilter: backgroundAsset ? 'blur(12px)' : undefined,
        }}
      />

      {/* Main scrollable content area */}
      <div className="interstitial-card-stack-content">
        <div className="interstitial-card-stack-layout">
          <div className="interstitial-card-stack-spacer" />

          <div className="flex w-full flex-col items-center gap-6">
          <div className="interstitial-card-stack-heading">{title}</div>

          <div className="interstitial-card-stack-viewport">
            {/* Static background card stack */}
            <div className="interstitial-card-stack-deck">
              <div className="interstitial-card-stack-deck-layer-1" />
              <div className="interstitial-card-stack-deck-layer-2" />
            </div>

            {cards.map((card, index) => {
              const isCurrentCard = index === cardState.currentCardIndex;
              const isAnimatingCard = index === cardState.animatingCardIndex;

              // Smart rendering logic based on drag direction and animation state
              let shouldRender = false;

              if (cardState.animatingCardIndex !== null) {
                // During animation: ONLY show the current card and the card sliding away
                shouldRender = index === cardState.currentCardIndex || index === cardState.animatingCardIndex;
              } else {
                // Normal state: show current card + adjacent card based on drag direction
                const isNextCard = index === cardState.currentCardIndex + 1 && index < cards.length;
                const isPrevCard = index === cardState.currentCardIndex - 1 && index >= 0;

                shouldRender =
                  index === cardState.currentCardIndex ||
                  (dragDirection === 'forward' && isNextCard) ||
                  (dragDirection === 'backward' && isPrevCard);
              }

              // Extra defensive check: if we're animating and this card is not involved, force hide
              if (
                cardState.animatingCardIndex !== null &&
                index !== cardState.currentCardIndex &&
                index !== cardState.animatingCardIndex
              ) {
                shouldRender = false;
              }

              if (!shouldRender) return null;

              // Strategic z-index calculation to prevent layering bugs
              let zIndex: number;
              if (isAnimatingCard) {
                // Animating card slides away behind new card but above background
                zIndex = 5;
              } else if (isCurrentCard) {
                // Current card always on top
                zIndex = 20;
              } else {
                // Adjacent cards should be below current card
                zIndex = 8;
              }

              return (
                <motion.div
                  key={`card-${index}-${card.title}`}
                  drag={isAnimatingCard ? false : isCurrentCard ? 'x' : false}
                  dragConstraints={{ left: -DRAG_CONSTRAINT, right: DRAG_CONSTRAINT }}
                  dragElastic={0.5}
                  dragMomentum={false}
                  dragTransition={{ bounceStiffness: 600, bounceDamping: 10 }}
                  onDrag={(
                    e: MouseEvent | TouchEvent | PointerEvent,
                    { offset }: { offset: { x: number; y: number } },
                  ) => {
                    // Track drag direction for smart adjacent card rendering
                    if (Math.abs(offset.x) > DRAG_DETECTION_THRESHOLD) {
                      setDragDirection(offset.x < 0 ? 'forward' : 'backward');
                    }
                  }}
                  onDragEnd={(
                    e: MouseEvent | TouchEvent | PointerEvent,
                    { offset, velocity }: { offset: { x: number; y: number }; velocity: { x: number; y: number } },
                  ) => {
                    const swipe = swipePower(offset.x, velocity.x);

                    if (swipe < -SWIPE_CONFIDENCE_THRESHOLD) {
                      handleNextCard();
                    } else if (swipe > SWIPE_CONFIDENCE_THRESHOLD && cardState.currentCardIndex > 0) {
                      handlePreviousCard();
                    }

                    // Clear drag direction when drag ends
                    setDragDirection(null);
                  }}
                  className="interstitial-card-stack-card"
                  style={{ zIndex }}
                  animate={{
                    x: isAnimatingCard ? (cardState.direction === 'forward' ? -SLIDE_DISTANCE : SLIDE_DISTANCE) : 0,
                    opacity: isAnimatingCard ? 0 : 1,
                  }}
                  initial={{ x: 0, opacity: 1 }}
                  transition={{
                    x: { type: 'spring', stiffness: 180, damping: 25 },
                    opacity: { duration: 0.3, ease: 'easeOut' },
                  }}
                  onAnimationComplete={isAnimatingCard ? handleAnimationComplete : undefined}
                >
                  <div className="interstitial-card-stack-card-body">
                    {card.image && <div className="interstitial-card-stack-card-image-wrapper">{graphic}</div>}

                    <h2 className="interstitial-card-stack-card-title">{card.title}</h2>

                    <p className="interstitial-card-stack-card-description">{card.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {cards.length > 1 && (
            <div className="interstitial-card-stack-pagination">
              {cards.map((_, index) => {
                const isActive = index === cardState.currentCardIndex;

                return (
                  <button
                    key={index}
                    onClick={() => handleDotClick(index)}
                    className={`interstitial-card-stack-pagination-dot ${
                      isActive
                        ? 'interstitial-card-stack-pagination-dot-active'
                        : 'interstitial-card-stack-pagination-dot-inactive'
                    }`}
                    aria-label={`Go to card ${index + 1}`}
                  >
                    {/* Progress fill overlay for auto-advance on active dot */}
                    {isActive && isAutoRotating && (
                      <motion.div className="interstitial-card-stack-pagination-fill" style={{ width: fillWidth }} />
                    )}

                    {/* Static full fill for active dot when not auto-rotating */}
                    {isActive && !isAutoRotating && (
                      <div className="interstitial-card-stack-pagination-fill-complete" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

          <div className="interstitial-card-stack-spacer" />
        </div>
      </div>

      {/* Sticky button at bottom */}
      <StickyFooterBar variant="dark" onBack={onBack} isBackDisabled={isBackDisabled}>
        <NextButton text="Next" onNext={handleNextCard} variant="primary" />
      </StickyFooterBar>
    </div>
  );
};
